import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ConfigurationType } from 'data/types';
import { setEquipmentFilterMemory, setEquipmentFilters } from 'features/ui';
import { useCallback, useEffect, useMemo } from 'react';
import {
  extendFilterReconcileMemory,
  SystemListFilterState,
  SystemListFilterUniverse
} from './equipment-comparison-filters';
import {
  equipmentFiltersToFilterState,
  filterStateToEquipmentFilters
} from './equipment-filter-conversion';
import {
  filterReconcileMemoryFromState,
  filterReconcileMemoryToState,
  isSameEquipmentFilterMemory
} from './equipment-filter-memory';

type UseSystemListFilterStateResult = readonly [
  SystemListFilterUniverse,
  SystemListFilterState,
  (next: SystemListFilterState) => void
];

export default function useSystemListFilterState(
  configurations: ConfigurationType[],
  rawUniverse: SystemListFilterUniverse
): UseSystemListFilterStateResult {
  const dispatch = useAppDispatch();
  const equipmentFilters = useAppSelector((state) => state.ui.equipmentFilters);
  const persistedMemory = useAppSelector(
    (state) => state.ui.equipmentFilterMemory
  );

  const configIds = useMemo(
    () => configurations.map((c) => c.uniqueId),
    [configurations]
  );

  // configIds memory is pinned to the live ids (dead ids from a
  // configurations reset would otherwise block the all-selected -> null
  // collapse forever); the other dimensions stay union-only to retain
  // hidden-option state.
  const workingMemory = useMemo(() => {
    const memory = extendFilterReconcileMemory(
      filterReconcileMemoryFromState(persistedMemory),
      configIds,
      rawUniverse
    );
    return { ...memory, configIds: new Set(configIds) };
  }, [persistedMemory, configIds, rawUniverse]);

  const nextPersistedMemory = useMemo(
    () => filterReconcileMemoryToState(workingMemory),
    [workingMemory]
  );

  useEffect(() => {
    if (!isSameEquipmentFilterMemory(nextPersistedMemory, persistedMemory)) {
      dispatch(setEquipmentFilterMemory(nextPersistedMemory));
    }
  }, [dispatch, nextPersistedMemory, persistedMemory]);

  // Display membership follows the config-scoped raw universe (checked
  // configurations only). The reconcile memory still tracks checked state
  // for options that are currently hidden, so re-checking a configuration
  // restores its options with their prior state.
  const memorizedUniverse = useMemo(
    () => ({
      airHandlerTypes: rawUniverse.airHandlerTypes,
      condenserSeries: rawUniverse.condenserSeries,
      condenserBtus: rawUniverse.condenserBtus,
      hyperHeat: rawUniverse.hyperHeat,
      systemTypes: rawUniverse.systemTypes,
      refrigerants: rawUniverse.refrigerants
    }),
    [rawUniverse]
  );

  const filterState = useMemo<SystemListFilterState>(
    () =>
      equipmentFiltersToFilterState(
        equipmentFilters,
        memorizedUniverse,
        configIds
      ),
    [equipmentFilters, memorizedUniverse, configIds]
  );

  const setFilterState = useCallback(
    (next: SystemListFilterState) => {
      dispatch(
        setEquipmentFilters(
          filterStateToEquipmentFilters(
            next,
            memorizedUniverse,
            workingMemory,
            equipmentFilters
          )
        )
      );
    },
    [dispatch, memorizedUniverse, workingMemory, equipmentFilters]
  );

  return [memorizedUniverse, filterState, setFilterState];
}
